import * as signalR from '@microsoft/signalr';
import config from 'react-global-configuration';
import { UserRegionDto } from '@trucktrax/trucktrax-ts-common';
import recordLog, { LogLevel } from '../../util/errorUtil';
import { store } from '../../Main';
import {
  clearSelectedMarker,
  closePopup,
  updatePosition,
  updateIncomingPosition,
  removePosition,
} from '../../store/actions/mapviewActions';
import { syncIncomingTicketStatus } from '../../store/actions/ticketActions';
import { allPromisesResolved, determineRegionId, getIdFromUrl } from '../../util/appUtil';
import {
  INCOMING_MATERIALS_ROUTE,
  JOIN_GROUP,
  RECEIVE_MESSAGE,
} from '../../constants/appConstants';
import {
  HTTP_BRIDGE_BASE_URL,
  TRACKING_SOCKET,
  UPDATE_POSITION_GROUP,
} from '../../constants/apiConstants';
import { getTokens, isExpired } from '../../util/authUtil';
import { ensureFreshAccessToken, RefreshOutcome } from '../requestService';
import { PositionDetailsDto } from '../../types';
import { shouldIgnorePositionBasedOnWorkflow } from '../../util/positionsUtil';
import { ConnectionState, isAuthFailureError, ReconnectScheduler } from '../../util/socketUtil';
import { getMapview } from '../mapviewsService';

export class TrackingSocket {
  private connectionState: ConnectionState;

  private regionUrl: string | null;

  private shouldReconnectOnClose: boolean = true;

  private hubConnection: signalR.HubConnection | null;

  /**
   * Owns backoff state (attempt counter + pending timer) for this socket.
   */
  private reconnectScheduler = new ReconnectScheduler();

  constructor() {
    this.connectionState = ConnectionState.Disconnected;
    this.hubConnection = null;
    this.regionUrl = null;
  }

  static onResponse = (response: string): void => {
    const { message: position } = JSON.parse(response) as {
      message: PositionDetailsDto | undefined;
    };
    if (!position) {
      return;
    }

    const state = store.getState();
    const { selectedMarker, currentRegion, selectedProductLine } = state;
    const isIncomingMaterialsTruck = TrackingSocket.isIncomingMaterialsTruck(position, state);

    const id = getIdFromUrl(position.trackedObject?.url) || position.driver?.id.toString() || '';

    // Update selected truck to null if truck becomes inactive. The `id` guard
    // avoids an empty-string match against a stray marker when both
    // trackedObject and driver are missing.
    if (id && id === selectedMarker?.markerId && position.active === false) {
      store.dispatch(clearSelectedMarker());
      const regionId = determineRegionId(currentRegion as UserRegionDto);
      if (!regionId) {
        recordLog(LogLevel.Warn, `Can not close marker ${id} without a valid region`);
      } else {
        store.dispatch(closePopup(id, regionId, selectedProductLine));
      }
    }

    // Inactive trucks belong on neither slice — clear both unconditionally
    // (workflow-ignored vs not is irrelevant when the truck is gone). Route
    // through UPDATE_* rather than REMOVE_* so the reducer matches the stale
    // entry via trackedObject/driver url; the backend can issue a fresh
    // `position.id` for the inactive ping, which would otherwise leave the
    // seeded marker stuck on the map.
    if (position.active === false) {
      store.dispatch(updatePosition(position));
      store.dispatch(updateIncomingPosition(position));
      return;
    }

    const workflowIgnored = shouldIgnorePositionBasedOnWorkflow(position);

    // Non-IM truck with a workflow-ignored status (e.g. external driver with
    // Unknown status, Returning for ReadyMix): drop from the standard map
    // and bail. IM trucks fall through so the IM slice still gets an update.
    if (workflowIgnored && !isIncomingMaterialsTruck) {
      store.dispatch(removePosition(position.id));
      return;
    }

    const inCurrentRegion =
      position.region?.url === currentRegion.url && position.productLine === selectedProductLine;
    // Route the update to the slice(s) it belongs to:
    //  - Standard map (positionList) wants in-region+productLine trucks
    //    whose workflow status is permitted. An IM truck that's also
    //    in-region but has a workflow-ignored status (e.g. Returning) must
    //    be removed from the standard slice — otherwise it would stay on
    //    the standard map in a state shouldDisplayPosition would have
    //    filtered out at fetch time.
    //  - IM map (incomingPositionList) doesn't apply the standard workflow
    //    filter — its display status is derived from ticket.currentStatus at
    //    the selector layer.
    if (inCurrentRegion && !workflowIgnored) {
      store.dispatch(updatePosition(position));
    } else if (inCurrentRegion && workflowIgnored) {
      store.dispatch(removePosition(position.id));
    }
    if (isIncomingMaterialsTruck) {
      store.dispatch(updateIncomingPosition(position));
      // Position frames are the only live signal for driver status
      // transitions between assignment and close — the IM sync group only
      // re-broadcasts on ticket mutations. Merge the status onto the
      // matching incoming ticket so the sidebar stays in step with the map.
      const ticketUrl = position.ticket?.url;
      if (ticketUrl && position.status) {
        store.dispatch(
          syncIncomingTicketStatus({
            ticketUrl,
            status: position.status,
            statusTimestamp: position.statusTimestamp,
          }),
        );
      }
    }
  };

  /**
   * True when the user is on the Incoming Materials page AND the position's
   * truck is referenced by an incoming ticket. In that case both the region
   * and workflow filters must be bypassed so cross-region trucks delivering
   * to a destination plant reach the map.
   *
   * Reads the route from `window.location.pathname` rather than
   * `state.geotraxPage.navRoute` because the latter only updates on explicit
   * nav clicks — browser back/forward and programmatic redirects leave it
   * stale, which would leak IM positions into the standard map's state.
   */
  private static isIncomingMaterialsTruck(
    position: PositionDetailsDto,
    state: ReturnType<typeof store.getState>,
  ): boolean {
    if (typeof window === 'undefined') return false;
    if (!window.location.pathname.includes(INCOMING_MATERIALS_ROUTE)) return false;

    // External/GT-Lite incoming trucks ping as drivers (no trackedObject),
    // so we also accept the ticket's truck url as the identifying truckId.
    const truckId = getIdFromUrl(position.trackedObject?.url ?? position.ticket?.truck?.url);
    if (!truckId) return false;

    const incomingTickets = state.incomingTicketsList ?? [];
    return incomingTickets.some(t => getIdFromUrl(t.truck?.url) === truckId);
  }

  async connect(regionUrl: string): Promise<void> {
    const { tenantId: tenant } = store.getState();
    let { accessToken } = getTokens();

    // do not try to connect if we're not logged in
    if (!accessToken) {
      return;
    }

    // An expired token 403s on /negotiate and spins the reconnect loop, so
    // refresh first. transient -> keep backing off; invalid -> user logged out.
    if (isExpired(accessToken)) {
      const refreshOutcome = await ensureFreshAccessToken();
      switch (refreshOutcome) {
        case RefreshOutcome.Refreshed:
          break;
        case RefreshOutcome.Transient:
          this.reconnect();
          return;
        case RefreshOutcome.Invalid:
          return;
        default:
          return;
      }
      accessToken = getTokens().accessToken;
      if (!accessToken) {
        return;
      }
    }

    const isRedundantConnectionAttempt =
      (this.regionUrl === regionUrl && this.connectionState === ConnectionState.Connecting) ||
      this.connectionState === ConnectionState.Connected;
    if (isRedundantConnectionAttempt) {
      return;
    }

    this.connectionState = ConnectionState.Connecting;
    this.regionUrl = regionUrl;

    const TRACKING_SOCKET_URL = `${config.get(HTTP_BRIDGE_BASE_URL)}${TRACKING_SOCKET}`;
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${TRACKING_SOCKET_URL}?tk=${accessToken}`, {
        accessTokenFactory: () => accessToken!,
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.hubConnection.serverTimeoutInMilliseconds = 30_000;
    this.hubConnection.on(RECEIVE_MESSAGE, message => {
      TrackingSocket.onResponse(message);
    });
    const localHubConnection = this.hubConnection;
    this.shouldReconnectOnClose = true;

    this.hubConnection.onclose(_ => {
      // Route through reconnect() to apply exponential backoff. Prior behavior
      // called connect() directly here, which produced a tight loop when the
      // server accepted and then immediately dropped the connection.
      const matchingConnection = this.hubConnection === localHubConnection;
      if (!matchingConnection) {
        return;
      }

      this.clearHubConnectionState(true);
      if (this.shouldReconnectOnClose) {
        this.reconnect();
      }
    });

    try {
      await localHubConnection.start();
      const group = `${UPDATE_POSITION_GROUP}${tenant}`;
      const groupJoinResponses = await Promise.allSettled([
        localHubConnection.invoke(JOIN_GROUP, group),
      ]);

      const allGroupsConnected = allPromisesResolved(groupJoinResponses);
      if (!allGroupsConnected) {
        throw new Error('Failed to establish all group connections forcing reconnect');
      }

      const matchingConnection = this.hubConnection === localHubConnection;
      if (matchingConnection) {
        this.connectionState = ConnectionState.Connected;
        await store.dispatch<any>(getMapview(regionUrl));
        // Successful handshake + group join: reset backoff so a later drop
        // recovers quickly rather than inheriting stale delay.
        this.reconnectScheduler.reset();
      }
    } catch (err) {
      recordLog('warn', 'Tracking Socket', err);
      const matchingConnection = this.hubConnection === localHubConnection;
      if (matchingConnection) {
        this.clearHubConnectionState(true);
        // A 401/403 here is the server refusing a token the client thinks is
        // valid (e.g. auth outage rejecting good tokens, DEV-4179): refresh,
        // then retry -- unless the session is definitively gone.
        if (isAuthFailureError(err)) {
          const refreshOutcome = await ensureFreshAccessToken();
          if (refreshOutcome === RefreshOutcome.Invalid) {
            return;
          }
        }
        this.reconnect();
      }
    }
  }

  /**
   * Queues the next reconnect attempt using capped exponential backoff.
   * Re-reads the current region from the Redux store at fire time so the
   * socket follows region changes that happened while we were disconnected.
   */
  private reconnect(): void {
    // Never reconnect over a live/opening socket -- a connect() that lands
    // Connected during the 403-catch refresh await must not be orphaned (PR #1217).
    if (
      this.connectionState === ConnectionState.Connecting ||
      this.connectionState === ConnectionState.Connected
    ) {
      return;
    }

    if (
      this.connectionState === ConnectionState.Reconnecting &&
      this.reconnectScheduler.isPending
    ) {
      return;
    }

    this.connectionState = ConnectionState.Reconnecting;
    this.reconnectScheduler.schedule(async () => {
      const { currentRegion } = store.getState();
      const regionUrl = currentRegion ? (currentRegion.url as string) : '';
      if (this.reconnectScheduler.currentAttempt >= 2) {
        // Rebuild the (possibly wedged) hub but preserve backoff -- resetting
        // it here pins persistent failures at a 2-4s retry storm (DEV-4179).
        await this.disconnect(true, { preserveBackoff: true });
        await this.connect(regionUrl);
        return;
      }
      await this.connect(regionUrl);
    });
  }

  public async disconnect(destroyHub?: boolean, { preserveBackoff = false } = {}): Promise<void> {
    this.shouldReconnectOnClose = false;
    if (!preserveBackoff) {
      this.reconnectScheduler.reset();
    }

    try {
      if (this.hubConnection) {
        await this.hubConnection.stop();
      }
    } catch (_: unknown) {
      // Suppress errors from stop()
    } finally {
      this.clearHubConnectionState(destroyHub);
      this.shouldReconnectOnClose = true;
    }
  }

  private clearHubConnectionState(destroyHub: boolean = false): void {
    if (this.connectionState !== ConnectionState.Disconnected) {
      this.connectionState = ConnectionState.Disconnected;
    }

    if (destroyHub && this.hubConnection) {
      this.hubConnection.off(RECEIVE_MESSAGE);
      this.hubConnection = null;
    }

    this.regionUrl = null;
  }

  public async attemptConnectionChange(regionUrl: string): Promise<void> {
    if (
      this.regionUrl &&
      this.regionUrl === regionUrl &&
      this.connectionState === ConnectionState.Connected
    ) {
      return;
    }

    await this.disconnect(true);
    await this.connect(regionUrl);
  }
}

export default new TrackingSocket();
